import * as XLSX from "xlsx";

export const REQUIRED_HEADERS = [
  "Unic_ID",
  "Tipo_id",
  "Identificacion",
  "Ap1",
  "Ap2",
  "Nom1",
  "Nom2",
  "Sexo",
  "Codmuni",
  "Mpio_residencia",
  "Cod_EAPB",
  "Zona",
  "Fecha_Nto",
];

export const OPTIONAL_HEADERS = [
  "Direccion",
  "Telefono",
  "Email",
];

export const REQUIRED_VALUES = [
  "Tipo_id",
  "Identificacion",
  "Ap1",
  "Nom1",
  "Sexo",
  "Codmuni",
  "Mpio_residencia",
  "Cod_EAPB",
  "Zona",
  "Fecha_Nto",
];

export const HEADER_ALIASES = {
  unicid: "Unic_ID",
  tipoid: "Tipo_id",
  identificacion: "Identificacion",
  ap1: "Ap1",
  ap2: "Ap2",
  nom1: "Nom1",
  nom2: "Nom2",
  sexo: "Sexo",
  codmuni: "Codmuni",
  mpioresidencia: "Mpio_residencia",
  codeapb: "Cod_EAPB",
  direccion: "Direccion",
  zona: "Zona",
  fechanto: "Fecha_Nto",
  telefono: "Telefono",
  email: "Email",
};

export const normalizeHeader = (value) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]/g, "")
    .toLowerCase();

export const normalizeCell = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().split("T")[0];
  }

  return String(value).trim();
};

export const normalizeMunicipioCode = (value) => {
  const normalized = normalizeCell(value).replace(/\.0+$/, "");

  if (/^\d{1,5}$/.test(normalized)) {
    return normalized.padStart(5, "0");
  }

  return normalized;
};

const buildIsoDate = (year, month, day) => {
  const numericYear = Number(year);
  const numericMonth = Number(month);
  const numericDay = Number(day);

  if (
    !Number.isInteger(numericYear) ||
    !Number.isInteger(numericMonth) ||
    !Number.isInteger(numericDay) ||
    numericYear < 1900 ||
    numericYear > 2100
  ) {
    return "";
  }

  const candidate = new Date(Date.UTC(numericYear, numericMonth - 1, numericDay));
  if (
    candidate.getUTCFullYear() !== numericYear ||
    candidate.getUTCMonth() + 1 !== numericMonth ||
    candidate.getUTCDate() !== numericDay
  ) {
    return "";
  }

  return `${String(numericYear).padStart(4, "0")}-${String(numericMonth).padStart(2, "0")}-${String(numericDay).padStart(2, "0")}`;
};

export const normalizeDateCell = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().split("T")[0];
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    const parsed = XLSX.SSF.parse_date_code(value);
    if (parsed?.y && parsed?.m && parsed?.d) {
      return buildIsoDate(parsed.y, parsed.m, parsed.d);
    }
  }

  const normalized = String(value).trim();
  if (normalized === "") {
    return "";
  }

  if (/^\d+(\.\d+)?$/.test(normalized)) {
    const numericValue = Number(normalized);
    if (Number.isFinite(numericValue)) {
      const parsed = XLSX.SSF.parse_date_code(numericValue);
      if (parsed?.y && parsed?.m && parsed?.d) {
        const isoDate = buildIsoDate(parsed.y, parsed.m, parsed.d);
        if (isoDate) {
          return isoDate;
        }
      }
    }
  }

  let match = normalized.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
  if (match) {
    return buildIsoDate(match[1], match[2], match[3]) || normalized;
  }

  match = normalized.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (match) {
    return buildIsoDate(match[3], match[2], match[1]) || normalized;
  }

  match = normalized.match(/^(\d{8})$/);
  if (match) {
    const compactValue = match[1];
    if (/^(19|20)\d{6}$/.test(compactValue)) {
      return (
        buildIsoDate(
          compactValue.slice(0, 4),
          compactValue.slice(4, 6),
          compactValue.slice(6, 8)
        ) || normalized
      );
    }

    if (/^\d{4}(19|20)\d{2}$/.test(compactValue)) {
      return (
        buildIsoDate(
          compactValue.slice(4, 8),
          compactValue.slice(2, 4),
          compactValue.slice(0, 2)
        ) || normalized
      );
    }
  }

  return normalized;
};

export const remapRow = (row) => {
  const mapped = {};

  Object.entries(row || {}).forEach(([key, value]) => {
    const canonical = HEADER_ALIASES[normalizeHeader(key)];
    if (canonical) {
      mapped[canonical] =
        canonical === "Codmuni" || canonical === "Mpio_residencia"
          ? normalizeMunicipioCode(value)
          : canonical === "Fecha_Nto"
            ? normalizeDateCell(value)
            : normalizeCell(value);
    }
  });

  const result = {};
  [...REQUIRED_HEADERS, ...OPTIONAL_HEADERS].forEach((header) => {
    result[header] = mapped[header] ?? "";
  });

  return result;
};

export const validateRowIdentifiers = (items) => {
  const errors = [];

  (items || []).forEach(({ rowNumber, row }) => {
    const tipoId = String(row?.Tipo_id ?? "").trim().toUpperCase();
    const identificacion = String(row?.Identificacion ?? "").trim();
    const unicId = String(row?.Unic_ID ?? "").trim().toUpperCase();
    const scientificNotationPattern = /^\d+(\.\d+)?e[+-]?\d+$/i;

    if (identificacion !== "" && scientificNotationPattern.test(identificacion)) {
      errors.push({
        row: rowNumber,
        identificacion,
        message:
          "La identificación está en notación científica. En Excel formatea la columna como Texto y vuelve a cargar el archivo.",
      });
      return;
    }

    if (identificacion !== "" && !/^[A-Za-z0-9]+$/.test(identificacion)) {
      errors.push({
        row: rowNumber,
        identificacion,
        message:
          "La identificación solo puede contener letras y números, sin espacios ni caracteres especiales.",
      });
      return;
    }

    const expectedUnic = `${tipoId}${identificacion}`.toUpperCase();
    if (tipoId !== "" && identificacion !== "" && unicId !== "" && unicId !== expectedUnic) {
      errors.push({
        row: rowNumber,
        identificacion,
        message: `Unic_ID inválido. Debe ser ${expectedUnic}.`,
      });
    }
  });

  return errors;
};

export const parseExcelBuffer = (buffer) => {
  const workbook = XLSX.read(buffer, { type: "array", cellDates: true });
  const worksheet = workbook.Sheets[workbook.SheetNames[0]];
  const rawHeaderRows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: "" });
  const headerRow = Array.isArray(rawHeaderRows[0]) ? rawHeaderRows[0] : [];

  const foundHeaders = new Set(
    headerRow.map((header) => HEADER_ALIASES[normalizeHeader(header)]).filter(Boolean)
  );

  const missingHeaders = REQUIRED_HEADERS.filter((header) => !foundHeaders.has(header));
  if (missingHeaders.length > 0) {
    return {
      missingHeaders,
      normalizedRows: [],
      identifierErrors: [],
    };
  }

  const sheetRows = XLSX.utils.sheet_to_json(worksheet, {
    defval: "",
    raw: false,
  });

  const remapped = sheetRows
    .map((rawRow, index) => ({
      rowNumber: index + 2,
      row: remapRow(rawRow),
    }))
    .filter(({ row }) => Object.values(row).some((value) => String(value).trim() !== ""));

  const identifierErrors = validateRowIdentifiers(remapped);

  return {
    missingHeaders,
    normalizedRows: remapped.map(({ row }) => row),
    identifierErrors,
  };
};
